import type {
  AutomationTestHitDto,
  CaseAutomationLinkDto,
  CaseLinkResultStatus,
} from '@tms/shared';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, Bot, Code2, Link2, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  ErrorState,
  SearchField,
  SkeletonRows,
  Dialog,
} from '../../../components/ui';
import { useErrorText, useFormat, useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { useDebounced } from '../../../lib/use-debounced';
import { useAutomationTeams } from '../api';
import { CauseBadge } from '../ui';
import { useAddCaseLink, useCaseAutomation, useRemoveCaseLink, useTestSearch } from './api';

const RESULT_STYLES: Record<CaseLinkResultStatus, string> = {
  passed: 'bg-pass-soft text-pass',
  failed: 'bg-fail-soft text-fail',
  broken: 'bg-fail-soft text-fail',
  skipped: 'bg-skip-soft text-skip',
  missing: 'bg-sunken text-ink-muted',
};

/** How the linked autotest went last time, with a way to the run. */
const LastResult = ({ link }: { link: CaseAutomationLinkDto }) => {
  const { t } = useT();
  const { relative } = useFormat();
  const last = link.last;
  if (!last)
    return <span className="text-[12px] text-ink-subtle">{t('automation.caseLinks.noRuns')}</span>;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge className={RESULT_STYLES[last.status]}>
        {t(`automation.caseLinks.result.${last.status}`)}
      </Badge>
      {last.counts.total > 1 ? (
        <span className="text-[12px] text-ink-muted">
          {t('automation.caseLinks.counts', {
            passed: last.counts.passed,
            total: last.counts.total,
          })}
        </span>
      ) : null}
      <CauseBadge cause={last.cause} />
      <Link
        to="/automation/runs/$runId"
        params={{ runId: last.pipelineId }}
        search={{ project: link.automationProjectId }}
        className="text-[12px] text-accent hover:underline"
      >
        #{last.pipelineId}
      </Link>
      {last.at ? <span className="text-[12px] text-ink-subtle">{relative(last.at)}</span> : null}
      {last.message ? (
        <p className="w-full whitespace-pre-wrap break-words text-[12px] text-fail">
          {last.message}
        </p>
      ) : null}
    </div>
  );
};

const linkKey = (link: {
  automationProjectId: string;
  className: string;
  testMethod: string | null;
}) => `${link.automationProjectId}|${link.className}|${link.testMethod ?? ''}`;

/**
 * Finds a class or a test by words of its name across every scanned project —
 * «QR оплачивается», a class or a repository — instead of walking project →
 * class → test.
 */
const LinkDialog = ({
  caseKey,
  linked,
  open,
  onOpenChange,
}: {
  caseKey: string;
  /** what the case is already linked to */
  linked: Set<string>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useT();
  const { errorText } = useErrorText();
  const [search, setSearch] = useState('');
  const query = useDebounced(search.trim());
  const [chosen, setChosen] = useState<AutomationTestHitDto | null>(null);
  const found = useTestSearch(query, open);
  const add = useAddCaseLink(caseKey);
  const hits = found.data?.hits ?? [];

  const reset = () => {
    setSearch('');
    setChosen(null);
  };
  const submit = () =>
    chosen &&
    add.mutate(
      {
        automationProjectId: chosen.automationProjectId,
        className: chosen.className,
        testMethod: chosen.testMethod,
      },
      {
        onSuccess: () => {
          toast.success(t('automation.caseLinks.linked'));
          reset();
          onOpenChange(false);
        },
        onError: (error) => toast.error(errorText(error)),
      },
    );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title={t('automation.caseLinks.linkTitle', { key: caseKey })}
      description={t('automation.caseLinks.linkHint')}
      size="md"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={!chosen || add.isPending} onClick={submit}>
            {t('automation.caseLinks.link')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex">
          <SearchField
            value={search}
            onChange={(value) => {
              setSearch(value);
              setChosen(null);
            }}
            placeholder={t('automation.caseLinks.searchPlaceholder')}
            label={t('automation.caseLinks.searchLabel')}
            autoFocus
          />
        </div>
        {query.length < 2 ? (
          <p className="py-2 text-[13px] text-ink-subtle">
            {t('automation.caseLinks.searchStart')}
          </p>
        ) : found.error ? (
          <ErrorState error={found.error} onRetry={() => void found.refetch()} />
        ) : !found.data ? (
          <SkeletonRows rows={3} className="p-0" />
        ) : hits.length === 0 ? (
          <p className="py-2 text-[13px] text-ink-subtle">{t('automation.caseLinks.searchNone')}</p>
        ) : (
          <ul
            className="flex max-h-80 flex-col overflow-y-auto"
            aria-label={t('automation.caseLinks.searchResults')}
          >
            {hits.map((hit) => {
              const key = linkKey(hit);
              const already = linked.has(key);
              const on = chosen !== null && linkKey(chosen) === key;
              return (
                <li key={key}>
                  <button
                    type="button"
                    disabled={already}
                    aria-pressed={on}
                    onClick={() => setChosen(hit)}
                    className={cn(
                      'flex w-full flex-col gap-0.5 rounded-md px-2 py-2 text-left',
                      on ? 'bg-accent-soft' : 'hover:bg-sunken',
                      already && 'cursor-default opacity-60 hover:bg-transparent',
                    )}
                  >
                    <span className="flex flex-wrap items-center gap-2 text-[13px] text-ink">
                      {hit.testMethod ? hit.testName : (hit.classDisplayName ?? hit.className)}
                      {hit.testMethod ? null : (
                        <Badge>{t('automation.caseLinks.wholeClass')}</Badge>
                      )}
                      {hit.segment ? (
                        <Badge>
                          {hit.segment === 'IP'
                            ? t('automation.catalog.ip')
                            : t('automation.catalog.too')}
                        </Badge>
                      ) : null}
                      {already ? (
                        <span className="text-[12px] text-ink-subtle">
                          {t('automation.caseLinks.alreadyLinked')}
                        </span>
                      ) : null}
                    </span>
                    <span className="text-[12px] text-ink-muted">
                      {hit.projectName}
                      {hit.testMethod && hit.classDisplayName ? ` · ${hit.classDisplayName}` : ''}
                      <span className="ml-2 font-mono text-[11.5px] text-ink-subtle">
                        {hit.className}
                        {hit.testMethod ? `.${hit.testMethod}` : ''}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {found.data?.more && query.length >= 2 ? (
          <p className="text-[12px] text-ink-subtle">
            {t('automation.caseLinks.searchMore', { count: hits.length })}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
};

/**
 * The autotests of a test case: what covers it and how that went in the
 * latest run. A link from the code (@TmsLink) is changed in the code.
 */
export const CaseAutomationCard = ({ caseKey, canEdit }: { caseKey: string; canEdit: boolean }) => {
  const { t } = useT();
  const { errorText } = useErrorText();
  const automation = useCaseAutomation(caseKey);
  const teams = useAutomationTeams();
  const remove = useRemoveCaseLink(caseKey);
  const [linkOpen, setLinkOpen] = useState(false);
  const [removing, setRemoving] = useState<CaseAutomationLinkDto | null>(null);

  const links = useMemo(() => automation.data?.links ?? [], [automation.data]);
  const linked = useMemo(() => new Set(links.map(linkKey)), [links]);
  // Until autotest projects exist there is nothing to link: the block stays away instead of offering
  // an empty list (a case that already has links shows them regardless).
  const hasProjects = teams.data?.some((team) => team.projects.length > 0) ?? false;
  if (links.length === 0 && !hasProjects) return null;
  return (
    <Card>
      <CardHeader
        title={t('automation.caseLinks.title')}
        description={t('automation.caseLinks.hint')}
        action={
          canEdit ? (
            <Button size="sm" onClick={() => setLinkOpen(true)}>
              <Plus className="size-3.5" />
              {t('automation.caseLinks.add')}
            </Button>
          ) : undefined
        }
      />
      {automation.error ? (
        <ErrorState error={automation.error} onRetry={() => void automation.refetch()} />
      ) : !automation.data ? (
        <SkeletonRows rows={1} />
      ) : links.length === 0 ? (
        <CardBody>
          <p className="text-[13px] text-ink-subtle">{t('automation.caseLinks.none')}</p>
        </CardBody>
      ) : (
        <ul className="divide-y divide-border">
          {links.map((link) => (
            <li key={link.id} className="flex items-start gap-3 px-4 py-3">
              <Bot className="mt-0.5 size-4 shrink-0 text-ink-subtle" aria-hidden="true" />
              <div className="min-w-0 flex-1 space-y-1">
                <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
                  <Link
                    to="/automation/teams/$teamId/projects/$projectId"
                    params={{ teamId: link.teamId, projectId: link.automationProjectId }}
                    search={{ tab: 'classes' }}
                    className="font-medium text-ink hover:text-accent-fg"
                  >
                    {link.projectName}
                  </Link>
                  <span className="text-ink-subtle">/</span>
                  <span className="font-mono text-[12px] text-ink">
                    {link.className}
                    {link.testMethod ? `.${link.testMethod}` : ''}
                  </span>
                  <Badge
                    className={cn(link.source === 'code' && 'bg-accent-soft text-accent-fg')}
                    title={
                      link.source === 'code'
                        ? t('automation.caseLinks.fromCodeHint')
                        : t('automation.caseLinks.manualHint', { who: link.createdBy })
                    }
                  >
                    {link.source === 'code' ? (
                      <Code2 className="size-3" aria-hidden="true" />
                    ) : (
                      <Link2 className="size-3" aria-hidden="true" />
                    )}
                    {link.source === 'code'
                      ? t('automation.caseLinks.fromCode')
                      : t('automation.caseLinks.manual')}
                  </Badge>
                  {link.gone ? (
                    <Badge className="bg-fail-soft text-fail">
                      <AlertTriangle className="size-3" aria-hidden="true" />
                      {t('automation.caseLinks.gone')}
                    </Badge>
                  ) : null}
                </p>
                {link.gone ? (
                  <p className="text-[12px] text-fail">
                    {t(`automation.caseLinks.goneHint.${link.gone}`)}
                  </p>
                ) : null}
                {link.testName || link.classDisplayName ? (
                  <p className="text-[12px] text-ink-muted">
                    {link.testName ?? link.classDisplayName}
                  </p>
                ) : null}
                <LastResult link={link} />
              </div>
              {canEdit && link.source === 'manual' ? (
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={t('automation.caseLinks.remove')}
                  title={t('automation.caseLinks.remove')}
                  onClick={() => setRemoving(link)}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {canEdit ? (
        <LinkDialog caseKey={caseKey} linked={linked} open={linkOpen} onOpenChange={setLinkOpen} />
      ) : null}
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        title={t('automation.caseLinks.removeTitle')}
        description={t('automation.caseLinks.removeHint', {
          name: removing
            ? `${removing.className}${removing.testMethod ? `.${removing.testMethod}` : ''}`
            : '',
        })}
        confirmLabel={t('automation.caseLinks.remove')}
        loading={remove.isPending}
        onConfirm={() =>
          removing &&
          remove.mutate(removing.id, {
            onSuccess: () => setRemoving(null),
            onError: (error) => toast.error(errorText(error)),
          })
        }
      />
    </Card>
  );
};
